import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BoardSide,
  CardChoice,
  EmptyDomino,
  GameEvent,
  GameMeta,
  emptyBoard,
  emptyGameEvent,
} from "./engine/types";
import { getSuits } from "./engine/dominoMap";
import { Board } from "./ui/Board";
import { Hand } from "./ui/Hand";
import { OpponentBadge } from "./ui/Opponents";
import { ChatOverlay } from "./ui/ChatOverlay";
import { useIsPortrait } from "./ui/responsive";
import { Transport } from "./net/transport";
import { OnlineClient } from "./net/onlineClient";
import { LocalRunner } from "./net/localRunner";
import { ChatClient } from "./net/chatClient";

interface RuntimeState {
  event: GameEvent;
  yourSeat: number;
}

function decodeMeta(): GameMeta {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const gm = params.get("gm");
  if (!gm) return {};
  try {
    const decoded = atob(gm);
    return JSON.parse(decoded) as GameMeta;
  } catch {
    return {};
  }
}

function rotateForSeat(ev: GameEvent, seat: number): GameEvent {
  if (seat === 0) return ev;
  const r = (i: number) => (i + seat) % 4;
  const reorder = <T,>(arr: [T, T, T, T]): [T, T, T, T] => [
    arr[r(0)],
    arr[r(1)],
    arr[r(2)],
    arr[r(3)],
  ];
  return {
    ...ev,
    player: (ev.player - seat + 4) % 4,
    playersCardsRemaining: reorder(ev.playersCardsRemaining),
    playerWins: reorder(ev.playerWins),
    playerNames: reorder(ev.playerNames),
    playerHands: reorder(ev.playerHands),
  };
}

function legalSidesFor(card: number, ev: GameEvent): BoardSide[] {
  const board = ev.board;
  if (!board || !board.cardPosed) return ["posed"];
  const [s1, s2] = getSuits(card);
  const sides: BoardSide[] = [];
  if (s1 === board.leftSuit || s2 === board.leftSuit) sides.push("left");
  if (s1 === board.rightSuit || s2 === board.rightSuit) sides.push("right");
  return sides;
}

function eventMessage(ev: GameEvent, you: number): string | null {
  switch (ev.eventType) {
    case "roundWin":
      return ev.player === you
        ? "You win the round!"
        : `${ev.playerNames[ev.player]} wins the round`;
    case "roundDraw":
    case "roundInvalid":
      return "Round drawn";
    case "roundBlocked":
      return "Round blocked";
    case "gameWin":
      return ev.player === you ? "You win the game!" : `${ev.playerNames[ev.player]} wins the game`;
    case "passed":
      return ev.player === you ? "You pass" : `${ev.playerNames[ev.player]} passes`;
    default:
      return null;
  }
}

export const JsBoard: React.FC = () => {
  const meta = useMemo(decodeMeta, []);
  const portrait = useIsPortrait();
  const transportRef = useRef<Transport | null>(null);
  const chatRef = useRef<ChatClient | null>(null);
  const [chatClient, setChatClient] = useState<ChatClient | null>(null);

  const isLocal = meta.connectionType !== "online";
  const isSpectate = meta.actionType === "spectate";

  const yourSeat = 0;
  const [runtime, setRuntime] = useState<RuntimeState>(() => ({
    event: emptyGameEvent(),
    yourSeat,
  }));
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const [pendingSides, setPendingSides] = useState<BoardSide[] | null>(null);
  const [needsPose, setNeedsPose] = useState<"none" | "double-six" | "any">("none");
  const [transientMsg, setTransientMsg] = useState<string | null>(null);

  useEffect(() => {
    let transport: Transport;
    if (isLocal) {
      transport = new LocalRunner({
        randomSeed: meta.randomSeed ?? Date.now(),
        mode: meta.dominoGameMode === "partner" ? "partner" : "cutthroat",
        humanName: "You",
      });
    } else {
      transport = new OnlineClient({
        gameURL: meta.gameURL ?? "",
        gameId: meta.gameId ?? "",
        token: meta.webSocketToken ?? "",
        spectate: isSpectate,
      });
    }
    transportRef.current = transport;
    let msgTimer: ReturnType<typeof setTimeout> | null = null;
    const off = transport.onEvent((ev) => {
      const rotated = isLocal ? ev : rotateForSeat(ev, 0);
      setRuntime({ event: rotated, yourSeat: 0 });
      const msg = eventMessage(rotated, 0);
      if (msg) {
        if (msgTimer) clearTimeout(msgTimer);
        setTransientMsg(msg);
        msgTimer = setTimeout(() => setTransientMsg(null), 2200);
      }
    });
    transport.start();

    if (!isLocal && meta.gameURL && meta.gameId && meta.webSocketToken) {
      const c = new ChatClient({
        gameURL: meta.gameURL,
        gameId: meta.gameId,
        token: meta.webSocketToken,
        selfName: "You",
      });
      c.start();
      chatRef.current = c;
      setChatClient(c);
    }

    return () => {
      off();
      transport.stop();
      if (msgTimer) clearTimeout(msgTimer);
      if (chatRef.current) {
        chatRef.current.stop();
        chatRef.current = null;
      }
    };
  }, [isLocal, isSpectate, meta.gameId, meta.gameURL, meta.webSocketToken]);

  // Track pending pose vs play state for the local human player.
  useEffect(() => {
    if (!isLocal) return;
    const t = transportRef.current as LocalRunner | null;
    if (!t) return;
    t.human.onPendingChange = (p) => {
      if (!p) {
        setNeedsPose("none");
        setPendingSides(null);
        setSelectedCard(null);
        return;
      }
      if (p.kind === "play") setNeedsPose("none");
      if (p.kind === "pose") setNeedsPose("any");
      if (p.kind === "poseDS") setNeedsPose("double-six");
    };
    return () => {
      t.human.onPendingChange = () => {};
    };
  }, [isLocal]);

  const ev = runtime.event;
  const board = ev.board ?? emptyBoard();
  const yourHand: number[] = ev.playerHands[0] ?? [];
  const youCount = ev.playersCardsRemaining[0] ?? 0;

  const isYourTurnPlay = isLocal
    ? needsPose === "none" && (transportRef.current as LocalRunner | null)?.human.pending?.kind === "play"
    : !isSpectate && ev.player === 0 && ev.eventType === "playerTurn" && ev.gameState === "ept";

  const isYourTurnPose =
    isLocal && (needsPose === "any" || needsPose === "double-six");

  const playable = useMemo(() => {
    const set = new Set<number>();
    if (isYourTurnPose) {
      if (needsPose === "double-six") {
        set.add(27);
      } else {
        for (const c of yourHand) if (c !== EmptyDomino && c < 28) set.add(c);
      }
      return set;
    }
    if (!isYourTurnPlay) return set;
    if (!board.cardPosed) {
      for (const c of yourHand) if (c !== EmptyDomino && c < 28) set.add(c);
      return set;
    }
    for (const c of yourHand) {
      if (c === EmptyDomino) continue;
      const [s1, s2] = getSuits(c);
      if (
        s1 === board.leftSuit ||
        s2 === board.leftSuit ||
        s1 === board.rightSuit ||
        s2 === board.rightSuit
      )
        set.add(c);
    }
    return set;
  }, [isYourTurnPlay, isYourTurnPose, needsPose, yourHand, board]);

  const submitChoice = (choice: CardChoice) => {
    if (isLocal) {
      const t = transportRef.current as LocalRunner | null;
      if (!t) return;
      const p = t.human.pending;
      if (!p) return;
      if (p.kind === "play") t.human.submitPlay(choice.card, choice.boardSide as "left" | "right");
      else if (p.kind === "pose") t.human.submitPose(choice.card);
      else if (p.kind === "poseDS") t.human.submitPoseDoubleSix();
    } else {
      transportRef.current?.send(choice);
    }
  };

  const onPickCard = (card: number) => {
    if (!playable.has(card)) return;
    if (isYourTurnPose) {
      submitChoice({ card, boardSide: "posed" });
      setSelectedCard(null);
      setPendingSides(null);
      return;
    }
    if (!isYourTurnPlay) return;
    if (!board.cardPosed) {
      submitChoice({ card, boardSide: "posed" });
      setSelectedCard(null);
      return;
    }
    const sides = legalSidesFor(card, ev);
    if (sides.length === 1) {
      submitChoice({ card, boardSide: sides[0] });
      setSelectedCard(null);
      setPendingSides(null);
      return;
    }
    setSelectedCard(card);
    setPendingSides(sides);
  };

  const onPickSide = (side: BoardSide) => {
    if (selectedCard == null) return;
    submitChoice({ card: selectedCard, boardSide: side });
    setSelectedCard(null);
    setPendingSides(null);
  };

  const opponentSize = portrait ? 14 : 22;
  const turnHint =
    transientMsg ??
    (isYourTurnPose
      ? needsPose === "double-six"
        ? "Pose the double-six"
        : "Pose any tile"
      : isYourTurnPlay
      ? selectedCard != null && pendingSides && pendingSides.length > 1
        ? "Pick a side"
        : "Your turn — tap a tile"
      : ev.eventType === "playerTurn"
      ? `${ev.playerNames[ev.player] ?? "Opponent"}'s turn`
      : "");

  // Layout: a CSS grid with named areas.
  const grid: React.CSSProperties = portrait
    ? {
        display: "grid",
        gridTemplateRows: "auto 1fr auto",
        gridTemplateColumns: "auto 1fr auto",
        gridTemplateAreas: `
          ".  top  ."
          "left board right"
          ".  hand  ."
        `,
        gap: 6,
        padding: 6,
        height: "100vh",
        boxSizing: "border-box",
        background: "#0a1f0e",
        color: "#fff",
      }
    : {
        display: "grid",
        gridTemplateRows: "auto 1fr 22%",
        gridTemplateColumns: "auto 1fr auto",
        gridTemplateAreas: `
          ".  top  ."
          "left board right"
          "hand hand hand"
        `,
        gap: 8,
        padding: 8,
        height: "100vh",
        boxSizing: "border-box",
        background: "#0a1f0e",
        color: "#fff",
      };

  return (
    <div style={grid}>
      <div style={{ gridArea: "top", display: "flex", justifyContent: "center" }}>
        <OpponentBadge
          name={ev.playerNames[2] ?? "Top"}
          cardsRemaining={ev.playersCardsRemaining[2] ?? 0}
          wins={ev.playerWins[2] ?? 0}
          isCurrentTurn={ev.player === 2 && ev.eventType === "playerTurn"}
          orientation="top"
          size={opponentSize}
        />
      </div>
      <div style={{ gridArea: "left", display: "flex", alignItems: "center" }}>
        <OpponentBadge
          name={ev.playerNames[3] ?? "Left"}
          cardsRemaining={ev.playersCardsRemaining[3] ?? 0}
          wins={ev.playerWins[3] ?? 0}
          isCurrentTurn={ev.player === 3 && ev.eventType === "playerTurn"}
          orientation="left"
          size={opponentSize}
        />
      </div>
      <div style={{ gridArea: "board", position: "relative", minWidth: 0, minHeight: 0 }}>
        <Board
          board={board}
          pendingCard={selectedCard}
          pendingSides={pendingSides ?? undefined}
          onPickSide={onPickSide}
          message={transientMsg}
        />
        <ChatOverlay client={chatClient} selfName="You" />
      </div>
      <div style={{ gridArea: "right", display: "flex", alignItems: "center" }}>
        <OpponentBadge
          name={ev.playerNames[1] ?? "Right"}
          cardsRemaining={ev.playersCardsRemaining[1] ?? 0}
          wins={ev.playerWins[1] ?? 0}
          isCurrentTurn={ev.player === 1 && ev.eventType === "playerTurn"}
          orientation="right"
          size={opponentSize}
        />
      </div>
      <div style={{ gridArea: "hand", minHeight: 80 }}>
        <Hand
          hand={yourHand}
          selectedCard={selectedCard}
          playableCards={playable}
          onPickCard={onPickCard}
          isYourTurn={isYourTurnPlay || isYourTurnPose}
          hint={turnHint}
        />
      </div>
    </div>
  );
};

export default JsBoard;
